import { useEffect, useMemo, useRef, useState } from 'react';
import { RoamIconChromeOffline } from '@/components/icons/roamIconChrome';
import { InstallGuide } from '@/components/pwa/InstallGuide';
import { useAuth } from '@/lib/auth/client-auth';
import {
  isStandaloneDisplay,
  PWA_STANDALONE_START_PATH,
  shouldRedirectStandaloneFromMarketingHome,
} from '@/lib/install-platform';
import { clearPrivateOfflineData } from '@/lib/offline/storage';
import { useOnlineStatus } from '@/lib/offline/useOnlineStatus';

const CLERK_CLIENT_CONFIGURED = Boolean(
  process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY?.trim() &&
  !process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY?.includes('...') &&
  !process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY?.includes('<'),
);

export function PwaRuntime() {
  const online = useOnlineStatus();
  const [offlineMessage, setOfflineMessage] = useState('Reconnect to continue.');

  useEffect(() => {
    if (!isStandaloneDisplay()) return;
    if (
      !shouldRedirectStandaloneFromMarketingHome(window.location.pathname, window.location.search)
    ) {
      return;
    }
    window.location.replace(PWA_STANDALONE_START_PATH);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    if (process.env.NODE_ENV !== 'production') {
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(registrations.map((registration) => registration.unregister())),
        )
        .catch((error: unknown) => {
          console.warn('Roam service worker cleanup failed', error);
        });

      if ('caches' in window) {
        window.caches
          .keys()
          .then((names) =>
            Promise.all(
              names
                .filter((name) => name.startsWith('roam-static-'))
                .map((name) => window.caches.delete(name)),
            ),
          )
          .catch((error: unknown) => {
            console.warn('Roam cache cleanup failed', error);
          });
      }

      return;
    }

    navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      console.warn('Roam service worker registration failed', error);
    });
  }, []);

  useEffect(() => {
    const pathname = window.location.pathname;
    setOfflineMessage(
      /^\/trips\/[^/?#]+/i.test(pathname)
        ? "Saved trip views stay read-only until you're back online."
        : 'Reconnect to continue.',
    );
  }, []);

  const containerClass = useMemo(
    () =>
      'fixed left-[max(1rem,var(--safe-left))] right-[max(1rem,var(--safe-right))] z-[70] mx-auto max-w-md rounded-xl border shadow-lg backdrop-blur',
    [],
  );

  return (
    <>
      {CLERK_CLIENT_CONFIGURED ? <SessionOfflineCleanup /> : null}
      {!online ? (
        <div
          className={`${containerClass} bottom-[calc(var(--safe-bottom)+1rem)] border-sunlight/60 bg-white/95 p-3 text-sm text-ink`}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 font-semibold">
            <RoamIconChromeOffline className="h-4 w-4 text-primary" />
            You&apos;re offline.
          </div>
          <p className="mt-1 text-xs text-ink/65">{offlineMessage}</p>
        </div>
      ) : null}

      <InstallGuide suppressBanner={!online} />
    </>
  );
}

/** Clear account-scoped browser data on the real Clerk session transition. */
function SessionOfflineCleanup() {
  const { isLoaded, userId } = useAuth();
  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const clearingRef = useRef(false);

  useEffect(() => {
    if (!isLoaded) return;
    const previousUserId = previousUserIdRef.current;
    const currentUserId = userId ?? null;
    previousUserIdRef.current = currentUserId;

    // `undefined` is the first resolved auth state. Do not erase an existing
    // user's offline data during ordinary app hydration.
    if (previousUserId === undefined || previousUserId === currentUserId || !previousUserId) return;
    if (clearingRef.current) return;

    clearingRef.current = true;
    void clearPrivateOfflineData()
      .catch((error: unknown) => {
        console.warn('Unable to clear Roam offline data after the account changed', error);
      })
      .finally(() => {
        clearingRef.current = false;
      });
  }, [isLoaded, userId]);

  return null;
}
